import React, { FC, ReactElement, useContext, useEffect, useState } from 'react'
import { NotificationsLanguageModal } from '../../../i18n'
import { notificationSettingsPushToken } from '../../NonReact/constants/ApiConstants'
import { EMPTY_STRING } from '../../NonReact/constants/Common'
import { RESTMethods } from '../../NonReact/constants/REST'
import CLanguageHandler from '../../NonReact/Languages/LanguageHandler'
import { isVarNotEmpty } from '../../NonReact/utils/GlobalFunctions'
import { UserInfo, useUserInfo } from '../../ReactRouting/Hooks/useUserInfo'
import { StatusCodes } from '../../NonReact/common/API/REST/RESTAPIClient'
import { requestJSON } from '../../NonReact/common/API/REST/RESTClient'
import { IconName } from '../Common/Components/Icon'
import IconButton, { IconButtonType } from '../Common/Components/IconButton'
import { LanguageContext, MessageContext, Page } from '../Common/Contexts'
import { MessageContextType } from '../Common/Contexts/MessageContext'
import { UserSettings, useUserSettings } from '../Common/Hooks/useUserSettings'
import { ReactState } from '../Common/Types/Types'
import { $error } from '../Common/Utils/ConsoleUtils'
import {
    FIREBASE_VAPIDKEY,
    firebaseConfig,
    NotificationChannels,
    NotificationIconTypes,
    NotificationPermissions,
    NotificationPushServiceTypes
} from './Notifications/Constants'
import NotificationsModal from './Notifications/NotificationsModal'
import { NotificationIcons, NotificationIconsWithID, NotificationTokenRequest } from './Notifications/Types'
import { ApplicationResponseNotification, JSONResponse, RESTOptions } from '../../NonReact/common/Types/Types'
import { $akixi } from '../../NonReact/common/AkixiWindow'
import { getPageName } from '../../NonReact/common/DataIngest'

type Props = {
    disableIcon?: boolean
    isOpenState?: ReactState<boolean>
}

export const NotificationIcon: FC<Props> = ({ disableIcon, isOpenState }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(NotificationsLanguageModal.NAME)
    const { displayError }: MessageContextType = useContext(MessageContext)

    // eslint-disable-next-line
    const [firebaseObj, setFirebaseObj] = useState<any>(null)
    const [isOpen, setIsOpen]: ReactState<boolean> = isOpenState ?? useState<boolean>(false)
    const [isIconGrayedOut, setIsIconGrayedOut]: ReactState<boolean> = useState<boolean>(true)

    const [userInfo]: UserInfo = useUserInfo()
    const [userSettings, setUserSettings]: UserSettings = useUserSettings()

    const [displayNotification, setDisplayNotification]: ReactState<Nullable<string>> = useState<Nullable<string>>(null)

    const currentPageName: string = getPageName()

    const handleFirebaseTokenRequest = (
        notifcationSettings: ApplicationResponseNotification,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        getToken: any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        messaging: any
    ): void => {
        if (
            getToken &&
            notifcationSettings.DoNotDisturb === false &&
            notifcationSettings.NotificationChannels.includes(NotificationChannels.BROWSER_PUSH)
        ) {
            Notification.requestPermission().then((permission: NotificationPermission) => {
                if (permission === NotificationPermissions.GRANTED) {
                    // eslint-disable-next-line
                    getToken(messaging, {
                        vapidKey: FIREBASE_VAPIDKEY
                    })
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                        .then((currentToken: string) => {
                            if (currentToken) {
                                requestJSON(notificationSettingsPushToken, {
                                    method: RESTMethods.PUT,
                                    body: {
                                        Token: currentToken,
                                        PushService: NotificationPushServiceTypes.FCM
                                    },
                                    errorOnNoContent: false,
                                    additionalAllowableNoContentCodes: [
                                        StatusCodes.NOT_MODIFIED_CLIENT_REDIRECTION_CODE
                                    ]
                                } as RESTOptions<NotificationTokenRequest>).catch(displayError)
                            }
                        })
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                        .catch(displayError)
                }
            })
        }
    }

    const handleNotificationFetchRequest = (
        response: JSONResponse<ApplicationResponseNotification>,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        getToken: any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        messaging: any
    ): void => {
        setUserSettings(response ?? undefined)

        const isNotificationsSetup: boolean =
            response?.NotificationsTelSysID === null && response?.NotificationsTelSysID === null

        if (!isNotificationsSetup && response) handleFirebaseTokenRequest(response, getToken, messaging)

        if (isNotificationsSetup) return setDisplayNotification(NotificationIconTypes.ADD)

        return response?.DoNotDisturb
            ? setDisplayNotification(NotificationIconTypes.DISABLE)
            : setDisplayNotification(NotificationIconTypes.ENABLE)
    }

    const handleRefreshNotifications = (updatedNotifications: ApplicationResponseNotification): void => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        handleFirebaseTokenRequest(updatedNotifications, firebaseObj.getToken, firebaseObj.messaging)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        handleNotificationFetchRequest(updatedNotifications, firebaseObj.getToken, firebaseObj.messaging)
    }

    useEffect((): void => {
        if (currentPageName !== Page.WELCOME) {
            if (userInfo?.Scope?.IsDeviceTypeGroup) {
                setIsIconGrayedOut(false)
            } else {
                setIsIconGrayedOut(true)
            }
        }
    }, [userInfo])

    useEffect((): void => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (currentPageName !== Page.WELCOME && $akixi?.firebase) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const { firebase } = $akixi
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const { initializeApp, getMessaging, getToken } = firebase

            try {
                // eslint-disable-next-line
                const app = initializeApp(firebaseConfig)
                // eslint-disable-next-line
                const messaging = getMessaging(app)

                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                setFirebaseObj({ messaging, getToken })

                if (isVarNotEmpty(userSettings)) {
                    handleNotificationFetchRequest(userSettings, getToken, messaging)
                }
            } catch (error) {
                $error(error)
            }
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    }, [JSON.stringify($akixi?.firebase), currentPageName, userSettings])

    const handleNotificationClick = (): void => setIsOpen(true)

    const notificationIcons: NotificationIconsWithID[] = [
        {
            id: NotificationIconTypes.ADD,
            iconName: IconName.NOTIFICATION_ADD,
            title: language.get(NotificationsLanguageModal.IconAddTooltip),
            type: IconButtonType.BUTTON,
            onClick: (): void => handleNotificationClick()
        },
        {
            id: NotificationIconTypes.ENABLE,
            iconName: IconName.NOTIFICATION_ENABLED,
            title: language.get(NotificationsLanguageModal.IconEnabledTooltip),
            type: IconButtonType.BUTTON,
            onClick: (): void => handleNotificationClick()
        },
        {
            id: NotificationIconTypes.DISABLE,
            iconName: IconName.NOTIFICATION_DISABLED,
            title: language.get(NotificationsLanguageModal.IconDisabledTooltip),
            type: IconButtonType.BUTTON,
            onClick: (): void => handleNotificationClick()
        }
    ]

    const getNotificationIconProps = (): ReactElement => {
        if (displayNotification === null) return <></>

        const filteredNotification: NotificationIconsWithID = notificationIcons.find(
            (icon) => icon.id === displayNotification
        ) as NotificationIconsWithID

        const props: NotificationIcons = {
            iconName: filteredNotification.iconName,
            title: isIconGrayedOut ? EMPTY_STRING : filteredNotification.title,
            type: filteredNotification.type,
            onClick: filteredNotification.onClick
        } as NotificationIcons

        return (
            <IconButton
                disabledSubTitle={language.get(NotificationsLanguageModal.IconDisabled)}
                isEnabled={!isIconGrayedOut}
                {...props}
            />
        )
    }

    return isVarNotEmpty(firebaseObj) ? (
        <>
            {disableIcon ? <></> : getNotificationIconProps()}
            {isVarNotEmpty(userSettings) ? (
                <NotificationsModal
                    NotificationSettings={userSettings}
                    refreshNotifications={handleRefreshNotifications}
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                />
            ) : (
                <></>
            )}
        </>
    ) : (
        <></>
    )
}
